/**
 * Pure / async helpers extracted from useChartOfAccounts so the main hook
 * stays focused on state plumbing. No React state here.
 */

import { findOurBankAccount, hasDuplicateBankAccount } from './account-tree-utils'
import { type AccountFormData } from './chart-of-accounts-helpers'
import { subtypeToBankAccountType } from '../../constants/labels'
import {
  createAccount, updateAccount,
  type Account, type CreateAccountRequest,
} from '../../services/account-service'
import {
  createBankAccount, updateBankAccount, listBankAccounts,
  type BankAccount,
} from '../../services/bank-account-service'

import type { Toast } from '../../components/ui'

type AddToast = (opts: Omit<Toast, 'id'>) => void

// ─── Build requests ────────────────────────────────────────────────────────

export function buildUpdateAccountRequest(
  formData: AccountFormData,
  account: Account,
): { name: string; description?: string; parentId?: string; tags?: string[]; code?: string } {
  const updateData: { name: string; description?: string; parentId?: string; tags?: string[]; code?: string } = {
    name: formData.name,
  }
  if (formData.description) {updateData.description = formData.description}
  if (formData.parentId) {updateData.parentId = formData.parentId}
  updateData.tags = formData.tags
  if (formData.code && formData.code !== account.code) {updateData.code = formData.code}
  return updateData
}

export function buildCreateAccountRequest(
  formData: AccountFormData,
  isBankCash: boolean,
): CreateAccountRequest {
  const request: CreateAccountRequest = {
    code: formData.code,
    name: formData.name,
    accountType: formData.accountType,
    idempotencyKey: crypto.randomUUID(),
  }
  if (formData.description) {request.description = formData.description}
  if (formData.parentId) {request.parentId = formData.parentId}
  if (formData.isHeader) {request.isHeader = formData.isHeader}
  if (formData.currency) {request.currency = formData.currency}
  if (formData.accountSubtype) {request.accountSubtype = formData.accountSubtype}
  if (formData.tags.length > 0) {request.tags = formData.tags}
  if (isBankCash) {request.isControl = true}
  // Reserved / GST book identity
  if (formData.systemTag) {request.systemTag = formData.systemTag}
  if (formData.gstComponent) {
    request.gstComponent = formData.gstComponent
    request.gstDirection = formData.gstDirection || 'output'
    const rate = parseFloat(formData.gstRate)
    if (!Number.isNaN(rate) && rate > 0) {request.gstRate = rate}
  }
  return request
}

// ─── Save bank details ─────────────────────────────────────────────────────

export interface SaveBankDetailsParams {
  organizationId: string
  ledgerId: string
  baseCurrency: string
  coaAccountId: string
  formData: AccountFormData
  addToast: AddToast
}

interface BankFields {
  accountName: string
  nameAtBank: string
  bankName: string
  branch: string
  accountNumber: string
  ifscCode: string
  swiftCode: string
  iban: string
  routingNumber: string
  sortCode: string
}

function pickBankFields(formData: AccountFormData): BankFields {
  return {
    accountName: formData.name,
    nameAtBank: formData.nameAtBank,
    bankName: formData.bankName,
    branch: formData.branch,
    accountNumber: formData.accountNumber,
    ifscCode: formData.ifscCode,
    swiftCode: formData.swiftCode,
    iban: formData.iban,
    routingNumber: formData.routingNumber,
    sortCode: formData.sortCode,
  }
}

function findDuplicateBank(
  allBankAccounts: BankAccount[],
  ours: BankAccount | undefined,
  coaAccountId: string,
  formData: AccountFormData,
): BankAccount | undefined {
  return allBankAccounts.find(ba =>
    ba.accountNumber === formData.accountNumber
    && ba.bankName === formData.bankName
    && ba.id !== ours?.id
    && ba.accountId
    && ba.accountId !== coaAccountId)
}

async function updateExistingBankAccount(
  p: SaveBankDetailsParams,
  ours: BankAccount,
  bankFields: BankFields,
): Promise<void> {
  const result = await updateBankAccount(p.organizationId, ours.id, {
    ...bankFields,
    accountType: ours.accountType ?? subtypeToBankAccountType(p.formData.accountSubtype),
    currency: ours.currency ?? p.formData.currency ?? p.baseCurrency,
    isActive: ours.isActive ?? true,
    displayOrder: ours.displayOrder ?? 0,
    notes: ours.notes ?? '',
    accountId: p.coaAccountId,
  })
  if (result.error) {p.addToast({ variant: 'error', message: `Bank details: ${result.error.message}` })}
}

async function createNewBankAccount(
  p: SaveBankDetailsParams,
  bankFields: BankFields,
): Promise<void> {
  const result = await createBankAccount(p.organizationId, {
    ledgerId: p.ledgerId,
    accountType: subtypeToBankAccountType(p.formData.accountSubtype),
    ...bankFields,
    currency: p.formData.currency !== '' ? p.formData.currency : p.baseCurrency,
    accountId: p.coaAccountId,
  })
  if (result.error) {p.addToast({ variant: 'error', message: `Bank details: ${result.error.message}` })}
}

export async function saveBankDetails(p: SaveBankDetailsParams): Promise<void> {
  const bankFields = pickBankFields(p.formData)
  const bankResponse = await listBankAccounts(p.organizationId, { ledgerId: p.ledgerId, activeOnly: false })
  const allBankAccounts = bankResponse.data?.bankAccounts ?? []
  const ours = findOurBankAccount(allBankAccounts, p.coaAccountId, p.formData)
  if (hasDuplicateBankAccount(allBankAccounts, ours, p.coaAccountId, p.formData)) {
    const dup = findDuplicateBank(allBankAccounts, ours, p.coaAccountId, p.formData)
    p.addToast({
      variant: 'error',
      message: `Bank account '${p.formData.accountNumber}' at '${p.formData.bankName}' is already linked to another account (${dup?.accountName ?? ''}).`,
    })
    return
  }
  if (ours) {
    await updateExistingBankAccount(p, ours, bankFields)
  } else {
    await createNewBankAccount(p, bankFields)
  }
}

// ─── Update / Create ───────────────────────────────────────────────────────

export interface DoUpdateParams {
  organizationId: string
  account: Account
  formData: AccountFormData
  addToast: AddToast
}

export async function doUpdateAccount(p: DoUpdateParams): Promise<Account | null> {
  const response = await updateAccount(p.organizationId, p.account.id, buildUpdateAccountRequest(p.formData, p.account))
  if (response.error) {
    p.addToast({ variant: 'error', message: response.error.message })
    return null
  }
  return response.data ?? null
}

export interface DoCreateParams {
  organizationId: string
  formData: AccountFormData
  isBankCash: boolean
  addToast: AddToast
}

export async function doCreateAccount(p: DoCreateParams): Promise<Account | null> {
  const response = await createAccount(p.organizationId, buildCreateAccountRequest(p.formData, p.isBankCash))
  if (response.error) {
    p.addToast({ variant: 'error', message: response.error.message })
    return null
  }
  return response.data ?? null
}
